import BubbleChart from 'components/molecules/BubbleChart';
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Select, Switch, Table } from 'antd';
import styled from '@emotion/styled';
import tw from 'twin.macro';
import CopyToClipBoard from 'components/molecules/MicroIntractions/CopyTOClipBoard';
import { colorConstants } from 'config/colorConstants';
import Loader from 'components/molecules/Loader';
import ErrorBox from 'components/molecules/ErrorBox/ErrorBox';
import DownloadExcel from 'components/molecules/MicroIntractions/DownloadExcel';
import { portFolioAnalysisDownload } from 'parser/graphToExcelDownloadParser';
import { calculateContainerHeight, getAdditionalDataOnCopyClick } from 'utils/helperMethods';
import { trackEventMixpanel } from 'utils/mixpanel';
import {
    PORTFOLIO_ANALYSIS_CHART_BUBBLE_COLOR_DROPDOWN_CHANGED,
    PORTFOLIO_ANALYSIS_CHART_BUBBLE_SIZE_DROPDOWN_CHANGED,
    PORTFOLIO_ANALYSIS_CHART_DETAILS_TOGGLED,
    PORTFOLIO_ANALYSIS_CHART_GROUPBY_DROPDOWN_CHANGED,
    PORTFOLIO_ANALYSIS_CHART_COPY,
    PORTFOLIO_ANALYSIS_CHART_X_AXIS_DROPDOWN_CHANGED,
    PORTFOLIO_ANALYSIS_CHART_Y_AXIS_DROPDOWN_CHANGED,
    PORTFOLIO_ANALYSIS_CHART_EXPORT,
    PORTFOLIO_ANALYSIS_CHART_LEGEND_CLICKED,
    PORTFOLIO_ANALYSIS_CHART_LEGEND_DROPDOWN_CLICKED,
} from 'utils/mixpanelEventNames';
import DownSvg from 'assets/svg/Down';
import { UserContext } from 'contexts/ConfigContext';

const CustomSelect = styled(Select)`
    ${tw`border border-purpleFont rounded text-[13px] text-headerColor`}
    .ant-select-selector {
        height: 100% !important;
        box-shadow: none !important;
        border: none !important;
        display: flex;
        align-items: center;
    }
    .ant-select-selection-search-input {
        height: 100% !important;
    }
    .ant-select-arrow {
        ${tw`text-headerColor`}
    }
`;

const chartHeight = calculateContainerHeight(1, 16 * 13);

function PortFolioAnaDriver(props: any) {
    const {
        chartData,
        kpiListData,
        onGroupByDropdownChange,
        groupBy1,
        groupByOptions,
        loading,
        error,
        groupBy2,
        yAxis,
        setYAxis,
        xAxisVal,
        setXAxisval,
        bubbleSize,
        setBubbleSize,
        showChartLabel,
        setShowChartLabel,
        allCheckedAttribute,
        timeSelectionLabel,
    } = props;
    const BubbleColorOptions = [
        { value: 'tactics', label: 'Tactics' },
        { value: 'mechanics', label: 'Mechanics' },
    ];

    const [bubbleColor, setBubbleColor] = useState(BubbleColorOptions[0]);
    const { appConfig: config } = useContext(UserContext);
    const { portfolioAnalysis } = config;
    const chartColors = portfolioAnalysis.chartColors;

    let labelRef = useRef(showChartLabel);
    const refForBubble = useRef(null);

    const memoisedLabels = useMemo(() => {
        return [xAxisVal?.value, yAxis?.value, bubbleColor?.value, bubbleSize?.value, bubbleSize?.value, 'name'];
    }, [xAxisVal, yAxis, bubbleColor, bubbleSize]);

    // IF ERROR RETURN ERROR MESSAGE
    if (error && !loading) return <ErrorBox msg={String(error)} />;

    // IF LOADING RETURN LOADER
    if (loading)
        return (
            <div style={{ height: 700, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <Loader />
            </div>
        );

    return (
        <div>
            {kpiListData && (
                <div className="flex justify-between mt-4">
                    <div className="flex gap-4">
                        <div className="flex gap-2 items-center">
                            <span className="font-xs">Group By</span>
                            <CustomSelect
                                suffixIcon={<DownSvg />}
                                className="dropdown-height dropdown-width uniqSelROIGroupByDropdown"
                                value={groupBy1?.value}
                                options={groupByOptions?.filter((option: any) => {
                                    if (option?.value !== groupBy2?.value) return option;
                                })}
                                onChange={(val: any, item: any) => {
                                    onGroupByDropdownChange(item, groupBy2);
                                    trackEventMixpanel(PORTFOLIO_ANALYSIS_CHART_GROUPBY_DROPDOWN_CHANGED, {
                                        dropdownName: item?.label,
                                    });
                                }}
                            />
                        </div>
                        <div className="flex gap-2 items-center">
                            <span className="font-xs">Bubble Color</span>
                            <CustomSelect
                                suffixIcon={<DownSvg />}
                                className="dropdown-height dropdown-width uniqSelROIBubbleColorDropdown"
                                value={groupBy2?.value}
                                options={groupByOptions?.filter((option: any) => {
                                    if (option?.value !== groupBy1?.value) return option;
                                })}
                                onChange={(val: any, item: any) => {
                                    onGroupByDropdownChange(groupBy1, item);
                                    trackEventMixpanel(PORTFOLIO_ANALYSIS_CHART_BUBBLE_COLOR_DROPDOWN_CHANGED, {
                                        dropdownName: item?.label,
                                    });
                                }}
                            />
                        </div>
                    </div>
                    <div className="flex gap-4">
                        <div className="flex gap-2 items-center">
                            <span className="font-xs">X Axis</span>
                            <CustomSelect
                                suffixIcon={<DownSvg />}
                                className="dropdown-height dropdown-width uniqSelROIXAxisDropdown"
                                value={xAxisVal?.value}
                                options={kpiListData?.filter((option: any) => {
                                    if (option?.value !== yAxis?.value && option?.value !== bubbleSize?.value)
                                        return option;
                                })}
                                onChange={(val: any, item: any) => {
                                    setXAxisval(item);
                                    trackEventMixpanel(PORTFOLIO_ANALYSIS_CHART_X_AXIS_DROPDOWN_CHANGED, {
                                        dropdownName: item?.label,
                                    });
                                }}
                            />
                        </div>
                        <div className="flex gap-2 items-center">
                            <span className="font-xs">Y Axis</span>
                            <CustomSelect
                                suffixIcon={<DownSvg />}
                                className="dropdown-height dropdown-width uniqSelROIYAxisDropdown"
                                value={yAxis?.value}
                                options={kpiListData?.filter((option: any) => {
                                    if (option?.value !== xAxisVal?.value && option?.value !== bubbleSize?.value)
                                        return option;
                                })}
                                onChange={(val: any, item: any) => {
                                    setYAxis(item);
                                    trackEventMixpanel(PORTFOLIO_ANALYSIS_CHART_Y_AXIS_DROPDOWN_CHANGED, {
                                        dropdownName: item?.label,
                                    });
                                }}
                            />
                        </div>
                        <div className="flex gap-2 items-center">
                            <span className="font-xs">Bubble Size</span>
                            <CustomSelect
                                suffixIcon={<DownSvg />}
                                className="dropdown-height dropdown-width uniqSelROIBubbleSizeDropdown"
                                value={bubbleSize?.value}
                                options={kpiListData?.filter((option: any) => {
                                    if (option?.value !== xAxisVal?.value && option?.value !== yAxis?.value)
                                        return option;
                                })}
                                onChange={(val: any, item: any) => {
                                    setBubbleSize(item);
                                    trackEventMixpanel(PORTFOLIO_ANALYSIS_CHART_BUBBLE_SIZE_DROPDOWN_CHANGED, {
                                        dropdownName: item?.label,
                                    });
                                }}
                            />
                        </div>
                    </div>
                </div>
            )}
            <div className="border border-borderLine rounded-sm px-4 py-2 mt-4" ref={refForBubble}>
                <div className="flex justify-between items-center">
                    <span className="font-lg font-medium">Custom Driver Analysis</span>
                    <div className="flex items-center gap-4">
                        <div className="flex gap-2 items-center mr-[3px]">
                            <span className="font-xs">Details</span>
                            <Switch
                                style={{
                                    backgroundColor: showChartLabel ? colorConstants.violet : colorConstants.borderGrey,
                                }}
                                defaultChecked
                                onChange={() => {
                                    setShowChartLabel(!showChartLabel);
                                    trackEventMixpanel(PORTFOLIO_ANALYSIS_CHART_DETAILS_TOGGLED, {
                                        isSelected: !showChartLabel,
                                    });
                                }}
                                checked={showChartLabel}
                            />
                        </div>
                        <div className="flex gap-2">
                            <CopyToClipBoard
                                mixpanelEvent={() => trackEventMixpanel(PORTFOLIO_ANALYSIS_CHART_COPY)}
                                refOfComponent={refForBubble}
                            />
                            <DownloadExcel
                                mixpanelEvent={() => trackEventMixpanel(PORTFOLIO_ANALYSIS_CHART_EXPORT)}
                                excelData={portFolioAnalysisDownload(chartData)}
                                headers={[
                                    `Group By (${groupBy1?.label.toLowerCase()})`,
                                    `Bubble Color (${groupBy2?.label.toLowerCase()})`,
                                    'Incremental net sales',
                                    'Incremental profit',
                                    'Incremental retailer profit',
                                    'Investments',
                                    'Lift',
                                    'ROI',
                                    'Profit Pool',
                                    'Tpr ACV',
                                    'Display ACV',
                                    'Feature ACV',
                                    'F&D ACV',
                                    'Total ACV',
                                ]}
                                title={'PortFolioAnalysis'}
                            />
                        </div>
                    </div>
                </div>
                <div className="w-full py-3 mb-1 overflow-auto"></div>
                <div>
                    <>
                        <div className="pr-5 mt-[-80px] relative top-[138px] mb-2 font-xs font-medium text-greyFont mb-6">
                            {yAxis?.label}
                        </div>
                        <BubbleChart
                            colorList={chartColors?.length === 0 ? undefined : chartColors}
                            chartContainer={'bubble-portfolio-analysis'}
                            height={chartHeight}
                            dataset={chartData || []}
                            k={showChartLabel}
                            labelConfiguration={showChartLabel}
                            yAxisTitle={yAxis?.label}
                            xAsisTitle={xAxisVal?.label}
                            bubbleSizeTitle={bubbleSize?.label}
                            fields={memoisedLabels}
                            alter={false}
                            lineColor={colorConstants.borderGrey}
                            fill={'#171E3E'}
                            stroke={'#d9d9d9'}
                            loading={loading}
                            error={null}
                            mixpanelTrackLegend={(text: string | undefined) => {
                                //if text present, then trigger mixpanel api for legend , else trigger for legend dropdown
                                if (text) {
                                    trackEventMixpanel(PORTFOLIO_ANALYSIS_CHART_LEGEND_CLICKED, {
                                        legendItem: text,
                                    });
                                } else {
                                    trackEventMixpanel(PORTFOLIO_ANALYSIS_CHART_LEGEND_DROPDOWN_CLICKED);
                                }
                            }}
                        />
                    </>
                </div>
            </div>
        </div>
    );
}

export default PortFolioAnaDriver;
